const API_BASE: string = (process.env.REACT_APP_API_BASE || 'http://localhost:5000').replace(/\/$/, '');

export type TransmissionCategory = 'Feedback' | 'Bug' | 'Report' | 'Idea' | 'Contact';
export type BlackboxCodeType = 'generated' | 'custom';
export type BlackboxAccessMode = 'read_only' | 'read_write';

export type TransmissionPayload = {
    category: TransmissionCategory;
    message: string;
    contact?: string;
    codeType?: BlackboxCodeType;
    customCode?: string;
    senderPassword?: string;
    receiverPassword?: string;
    accessMode?: BlackboxAccessMode;
    expiresInHours?: number | null;
};

export type TransmissionResponse = {
    success: boolean;
    receiptCode: string;
    codeType: BlackboxCodeType;
    accessMode: BlackboxAccessMode;
    expiresAt?: string | null;
    status: string;
    message: string;
};

export type TransmissionLookup = {
    receiptCode: string;
    codeType: BlackboxCodeType;
    accessMode: BlackboxAccessMode;
    expiresAt?: string | null;
    expired?: boolean;
    viewerRole?: 'sender' | 'receiver';
    category: TransmissionCategory;
    message?: string;
    status: string;
    statusLabel?: string;
    sealedByPresence?: boolean;
    presenceSealedAt?: string | null;
    presenceSeal?: string;
    canReply?: boolean;
    canReplyReason?: string;
    canAppeal?: boolean;
    thread?: BlackboxThreadEntry[];
    appealCount?: number;
    publishedAt?: string | null;
    createdAt: string;
    updatedAt: string;
};

export type TransmissionLookupResponse = {
    success: boolean;
    transmission: TransmissionLookup;
};

export type BlackboxCaseStatus = 'received' | 'in_review' | 'reply_requested' | 'reply_back' | 'rejected' | 'posted' | 'closed';
export type BlackboxAdminStatus = 'all' | BlackboxCaseStatus;

export type BlackboxThreadEntry = {
    id: string;
    author: 'transmitter' | 'presence';
    participantRole?: '' | 'creator' | 'participant' | 'sender' | 'receiver';
    message: string;
    createdAt: string;
};

export type BlackboxAdminTransmission = {
    id: string;
    receiptCode: string;
    codeType: BlackboxCodeType;
    accessMode: BlackboxAccessMode;
    expiresAt: string | null;
    expired: boolean;
    category: TransmissionCategory;
    message: string;
    contact: string;
    guestId: string;
    countryHint: string;
    locale: string;
    timezone: string;
    status: BlackboxCaseStatus;
    statusLabel: string;
    sealedByPresence: boolean;
    presenceSealedAt: string | null;
    presenceSeal: string;
    internalNote: string;
    thread: BlackboxThreadEntry[];
    appealCount: number;
    lastTransmitterReplyAt: string | null;
    lastPresenceReplyAt: string | null;
    rejectedAt: string | null;
    closedAt: string | null;
    reviewedBy: string | null;
    reviewedAt: string | null;
    publishedAt: string | null;
    publishedPost: string | null;
    createdAt: string;
    updatedAt: string;
};

export type BlackboxAdminListResponse = {
    success: boolean;
    transmissions: BlackboxAdminTransmission[];
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
};

export type BlackboxAdminUpdateResponse = {
    success: boolean;
    transmission: BlackboxAdminTransmission;
    postId?: string;
    message?: string;
};

function getGuestId() {
    const key = 'blackspace_guest_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const next = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, next);
    return next;
}

export async function sendTransmission(payload: TransmissionPayload): Promise<TransmissionResponse> {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/blackbox/transmissions`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            ...payload,
            guestId: getGuestId(),
            locale: navigator.language || '',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Transmission failed: ${res.status}`);
    }
    return data;
}

export async function checkSharedCodeAvailability(code: string, signal?: AbortSignal): Promise<{ success: boolean; available: boolean; message?: string }> {
    const cleanCode = code.trim().toUpperCase();
    const res = await fetch(`${API_BASE}/api/blackbox/shared-codes/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: cleanCode }),
        signal,
        cache: 'no-store',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Code check failed: ${res.status}`);
    }
    return data;
}

export async function lookupTransmission(receiptCode: string, accessPassword = ''): Promise<TransmissionLookupResponse> {
    const cleanCode = receiptCode.trim().toUpperCase();
    const res = await fetch(`${API_BASE}/api/blackbox/transmissions/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ receiptCode: cleanCode, accessPassword }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Receipt lookup failed: ${res.status}`);
    }
    return data;
}

export async function replyToTransmission(receiptCode: string, accessPassword: string, message: string): Promise<TransmissionLookupResponse & { message?: string }> {
    const cleanCode = receiptCode.trim().toUpperCase();
    const res = await fetch(`${API_BASE}/api/blackbox/transmissions/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            receiptCode: cleanCode,
            accessPassword,
            message,
        }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Reply failed: ${res.status}`);
    }
    return data;
}

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

async function parseAdminResponse<T>(res: Response): Promise<T> {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Blackbox request failed: ${res.status}`);
    }
    return data as T;
}

export async function listBlackboxTransmissions(params: {
    status?: BlackboxAdminStatus;
    page?: number;
    limit?: number;
    q?: string;
} = {}): Promise<BlackboxAdminListResponse> {
    const query = new URLSearchParams();
    query.set('status', params.status || 'all');
    query.set('page', String(params.page || 1));
    query.set('limit', String(params.limit || 20));
    if (params.q?.trim()) query.set('q', params.q.trim());

    const res = await fetch(`${API_BASE}/api/blackbox/admin/transmissions?${query.toString()}`, {
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return parseAdminResponse<BlackboxAdminListResponse>(res);
}

export async function updateBlackboxTransmissionStatus(
    id: string,
    status: BlackboxCaseStatus,
    internalNote?: string
): Promise<BlackboxAdminUpdateResponse> {
    const res = await fetch(`${API_BASE}/api/blackbox/admin/transmissions/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
        body: JSON.stringify({ status, internalNote }),
    });
    return parseAdminResponse<BlackboxAdminUpdateResponse>(res);
}

export async function replyFromSentinel(id: string, message: string): Promise<BlackboxAdminUpdateResponse> {
    const res = await fetch(`${API_BASE}/api/blackbox/admin/transmissions/${encodeURIComponent(id)}/replies`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
        body: JSON.stringify({ message }),
    });
    return parseAdminResponse<BlackboxAdminUpdateResponse>(res);
}

export async function publishBlackboxTransmission(id: string): Promise<BlackboxAdminUpdateResponse> {
    const res = await fetch(`${API_BASE}/api/blackbox/admin/transmissions/${encodeURIComponent(id)}/publish`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return parseAdminResponse<BlackboxAdminUpdateResponse>(res);
}
